import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Divider, Form, Typography } from 'antd';
const { Title, Text } = Typography;
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { FormDividerSpace } from 'client/ui/space/FormDividerSpace';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { useDispatch } from 'react-redux';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { AuthActions } from 'client/components/auth/AuthModule';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import {
  citizenCreateAccessTokenValidator,
  ICitizenCreateAccessTokenDto
} from 'common/dto/CitizenDto';
import { OpenWebLoginButtons } from 'client/components/auth/login/open-web/OpenWebLoginButtons';
import { useTranslation } from 'react-i18next';
import { SoapLdapLoginButton } from 'client/components/auth/login/soap-ldap/SoapLdapLoginButton';
import { UserLoginErrorAlert } from './UserLoginErrorAlert';
import qs from 'qs';

interface Params {
  id: string;
}

interface LocationState {
  from?: string;
}

export interface UserFrontLoginPageProps
  extends RouteComponentProps<Params, any, LocationState> {}

/**
 * Pagina di Login di un utente con email e password (senza SPID)
 */
export function UserFrontLoginPage(props: UserFrontLoginPageProps) {
  const [login, { loading }] = useApiMutation(CitizenApi.createAccessToken, {});
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fromUrl = props.location.state?.from;
  const queryString = qs.parse(props.location.search, {
    ignoreQueryPrefix: true
  });

  const errorMessage = queryString?.error as string | undefined;

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: 'Login'
  });

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <Formik<ICitizenCreateAccessTokenDto>
        enableReinitialize
        initialValues={citizenCreateAccessTokenValidator.cast()}
        validate={schemaToValidator(citizenCreateAccessTokenValidator)}
        onSubmit={async (values, helpers) => {
          const result = await login({ data: values });
          dispatch(AuthActions.loginSucceed('jwt', result.data.accessToken));

          // Se l'utente viene da una pagina che non sia di login ecc., lo
          // reindirizziamo lì.
          if (fromUrl && !fromUrl.startsWith('/utente')) {
            console.log(`[Redirect dopo login] ${fromUrl}`);
            props.history.push(fromUrl);
          }
        }}
      >
        {({ isSubmitting, submitForm, dirty }) => (
          <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
            <Card>
              <UserLoginErrorAlert message={errorMessage} />
              {/* Form Utente */}
              <FormikForm {...layout}>
                <Divider>{t('user.login.title', 'Effettua l’accesso')}</Divider>
                {/* Form Spid CIE eIDAS */}
                <OpenWebLoginButtons />

                <Divider>
                  {t('user.login.login-button-or-divider', 'OPPURE')}
                </Divider>
                <TextInputItem
                  label="Email"
                  name="email"
                  prefix={<UserOutlined style={{ color: '#d9d9d9' }} />}
                  placeholder="Email"
                />
                <TextInputItem
                  label="Password"
                  name="password"
                  type="password"
                  prefix={<LockOutlined style={{ color: '#d9d9d9' }} />}
                  placeholder="Password"
                />
                <Form.Item {...tailLayout}>
                  <Button
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    style={{ width: '100%' }}
                  >
                    {t('user.login.login-button', 'Accedi')}
                  </Button>
                  <p style={{ marginTop: '20px', marginBottom: 0 }}>
                    {t(
                      'user.login.sign-in-question',
                      'Non sei ancora registrato sul portale?'
                    )}{' '}
                    <Link to="/utente/registrazione">
                      {t(
                        'user.login.sign-in-link',
                        'Vai al modulo di registrazione.'
                      )}
                    </Link>
                  </p>
                  <p>
                    {t(
                      'user.login.password-forgot-question',
                      'Hai dimenticato la password?'
                    )}{' '}
                    <Link to="/utente/forgot">
                      {t(
                        'user.login.password-forgot-link',
                        'Reimposta password.'
                      )}
                    </Link>
                  </p>
                </Form.Item>
                {/* Accesso dipendenti */}
                <SoapLdapLoginButton />
              </FormikForm>
            </Card>
          </div>
        )}
      </Formik>
    </FrontLayout>
  );
}
